import { useEffect, useRef, useState } from 'react';
import { modUpdatesData } from '@/lib/data';
import { GitCommit, Rocket, Sparkles, ArrowRight, Check, Flame, DoorOpen } from 'lucide-react';
import { motion, AnimatePresence, useScroll, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Image from '@/components/Image';

type ModUpdate = (typeof modUpdatesData)[number];

/**
 * Lanzamiento destacado (Mod Cofrade 3.0). No es "próximamente": es la versión
 * que ya está en la calle, así que se saca del timeline y se muestra arriba del
 * todo con las capturas de la propia versión.
 */
function FeaturedRelease({ update }: { update: ModUpdate }) {
  const images = update.images || [];
  const [shot, setShot] = useState(0);

  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setShot((i) => (i + 1) % images.length), 5000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <section className="relative overflow-hidden py-16 md:py-24">
      {/* Resplandor de fondo */}
      <div className="pointer-events-none absolute left-1/2 top-0 h-[30rem] w-[60rem] max-w-full -translate-x-1/2 rounded-full bg-primary/10 blur-[140px]" />

      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="relative overflow-hidden rounded-3xl border-2 border-primary/40 bg-gradient-to-br from-primary/12 via-card/70 to-card/30 shadow-[0_0_80px_-20px_rgba(250,204,21,0.5)] backdrop-blur-sm"
        >
          {/* Filete dorado animado arriba */}
          <motion.div
            className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="grid gap-0 lg:grid-cols-[1.05fr_1fr]">
            {/* ── Galería ───────────────────────────────────────────────── */}
            <div className="relative aspect-[16/11] w-full overflow-hidden lg:aspect-auto lg:min-h-[32rem]">
              <AnimatePresence mode="sync">
                <motion.div
                  key={shot}
                  className="absolute inset-0"
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.4, ease: 'easeOut' }}
                >
                  <Image src={images[shot]} alt={`${update.version} — captura ${shot + 1}`} className="h-full w-full object-cover" />
                </motion.div>
              </AnimatePresence>

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-card/90 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-card/80" />

              {/* Puntitos de la galería */}
              {images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setShot(i)}
                      aria-label={`Captura ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${i === shot ? 'w-7 bg-primary' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ── Texto ─────────────────────────────────────────────────── */}
            <div className="relative flex flex-col justify-center gap-5 p-7 md:p-10">
              <div className="flex flex-wrap items-center gap-3">
                <motion.span
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-500/15 px-3 py-1 text-[0.65rem] font-black uppercase tracking-[0.2em] text-emerald-300"
                  animate={{ boxShadow: ['0 0 0 rgba(52,211,153,0)', '0 0 22px rgba(52,211,153,0.45)', '0 0 0 rgba(52,211,153,0)'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                  Ya disponible
                </motion.span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[0.65rem] font-black uppercase tracking-[0.2em] text-primary">
                  <Flame className="h-3 w-3" />
                  Última versión
                </span>
                <span className="text-xs font-semibold text-muted-foreground">{update.date}</span>
              </div>

              <div>
                <h2 className="font-headline text-4xl font-black uppercase leading-none tracking-tight md:text-6xl">
                  <span className="bg-gradient-to-b from-primary via-primary to-amber-600 bg-clip-text text-transparent drop-shadow-[0_4px_20px_rgba(250,204,21,0.35)]">
                    {update.version}
                  </span>
                </h2>
                <p className="mt-3 text-base text-muted-foreground md:text-lg">
                  La actualización más ambiciosa hasta la fecha. Ya no es una promesa: está en las calles de Jartiland.
                </p>
              </div>

              <ul className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
                {update.description.slice(1).map((desc, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: 0.25 + i * 0.05 }}
                    className="flex items-start gap-2 text-sm text-foreground/85"
                  >
                    <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                    <span>{desc}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-1 flex flex-wrap gap-3">
                <Button asChild size="lg" className="font-bold">
                  <Link to="/apply">
                    <DoorOpen className="h-4 w-4" />
                    Entrar al servidor
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="font-semibold">
                  <a href="https://discord.gg/KTMgF8pjz4" target="_blank" rel="noopener noreferrer">
                    <ArrowRight className="h-4 w-4" />
                    Discord del Mod Cofrade
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function UpdateCard({ update, index, isLast }: { update: ModUpdate; index: number; isLast: boolean }) {
  const isEven = index % 2 === 0;
  const isUpcoming = (update as any).upcoming === true;

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`relative flex flex-col md:flex-row ${isEven ? '' : 'md:flex-row-reverse'} items-center gap-2 md:gap-0`}
    >
      {/* Card */}
      <div className="w-full md:w-[calc(50%-2.5rem)]">
        <motion.div
          whileHover={{ y: -6 }}
          className={`group relative overflow-hidden rounded-2xl border p-6 backdrop-blur-sm transition-all duration-300 ${
            isUpcoming
              ? 'border-dashed border-primary/50 bg-gradient-to-br from-primary/10 via-card/60 to-card/40 shadow-[0_0_40px_rgba(250,204,21,0.15)]'
              : 'border-white/8 bg-card/40 hover:border-primary/30 hover:shadow-[0_8px_32px_rgba(0,0,0,0.35)]'
          }`}
        >
          {!isUpcoming && (
            <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500 opacity-0 transition-opacity group-hover:opacity-100" />
          )}

          {isUpcoming ? (
            <motion.div
              className="mb-4 flex items-center gap-2 text-primary"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-[0.25em]">Próximamente</span>
            </motion.div>
          ) : (
            update.images.length > 0 && (
              <div className="mb-5 overflow-hidden rounded-xl">
                {update.images.length > 1 ? (
                  <div className="grid grid-cols-2 gap-1">
                    {update.images.map((img, i) => (
                      <div key={i} className="relative aspect-square overflow-hidden">
                        <Image
                          src={img}
                          alt={`${update.version} - imagen ${i + 1}`}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="relative aspect-video overflow-hidden">
                    <Image
                      src={update.images[0]}
                      alt={update.version}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                )}
              </div>
            )
          )}

          <h3 className="font-headline text-2xl font-bold text-foreground">
            <span className={isUpcoming ? 'bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent' : 'text-primary'}>
              {update.version}
            </span>
          </h3>
          <p className="mb-4 mt-1 text-sm font-semibold text-muted-foreground">{update.date}</p>

          <ul className="space-y-2">
            {update.description.map((desc, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="mt-1 text-primary">•</span>
                <p>{desc}</p>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* Node */}
      <div className="relative z-10 flex h-16 w-16 flex-shrink-0 items-center justify-center md:absolute md:left-1/2 md:-translate-x-1/2">
        {/* Cable connecting node to card (desktop only) */}
        <div
          className={`absolute top-1/2 hidden h-0.5 w-10 -translate-y-1/2 bg-gradient-to-r md:block ${
            isEven ? 'right-1/2 from-transparent to-primary/60' : 'left-1/2 from-primary/60 to-transparent'
          }`}
        />
        {isUpcoming ? (
          <motion.div
            className="relative flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-primary bg-background/80 backdrop-blur-sm"
            animate={{ boxShadow: ['0 0 10px rgba(250,204,21,0.3)', '0 0 30px rgba(250,204,21,0.7)', '0 0 10px rgba(250,204,21,0.3)'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Rocket className="h-5 w-5 text-primary" />
          </motion.div>
        ) : (
          <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-amber-600 shadow-[0_0_15px_rgba(250,204,21,0.4)] ring-4 ring-background">
            <GitCommit className="h-4 w-4 text-primary-foreground" />
          </div>
        )}

        {/* Rope segment to next node (mobile only) */}
        {!isLast && (
          <div className="absolute left-1/2 top-full h-10 w-1 -translate-x-1/2 rounded-full bg-[repeating-linear-gradient(180deg,hsl(var(--primary)/0.8)_0px,hsl(var(--primary)/0.8)_6px,hsl(var(--primary)/0.2)_6px,hsl(var(--primary)/0.2)_12px)] md:hidden" />
        )}
      </div>

      <div className="hidden w-[calc(50%-2.5rem)] md:block" />
    </motion.div>
  );
}

export default function ModUpdatesPage() {
  const featured = modUpdatesData.find((u) => (u as any).featured === true);
  const timelineUpdates = [...modUpdatesData].filter((u) => u !== featured).reverse();
  const timelineRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start 0.2', 'end 0.8'],
  });
  const lineHeight = useSpring(useTransform(scrollYProgress, [0, 1], ['0%', '100%']), {
    stiffness: 80,
    damping: 25,
  });

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background">
      {/* Hero */}
      <section className="relative flex h-[70vh] min-h-[480px] flex-col items-center justify-center overflow-hidden text-center">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url('/images/modcofrade/fondomodcofrade.webp')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
        <div className="absolute -top-32 left-1/2 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-primary/15 blur-[140px]" />

        <div className="relative z-10 px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10"
          >
            <GitCommit className="h-7 w-7 text-primary" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-headline text-6xl font-extrabold uppercase tracking-tight md:text-8xl"
          >
            <span className="bg-gradient-to-b from-primary via-primary to-amber-600 bg-clip-text text-transparent drop-shadow-[0_4px_20px_rgba(250,204,21,0.35)]">
              Mod Cofrade
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-xl"
          >
            La evolución de nuestro mod, desde su creación hasta las últimas novedades. Cada versión, un paso más cerca de la Semana Santa perfecta.
          </motion.p>
        </div>
      </section>

      {/* Lanzamiento destacado: la versión actual, fuera del timeline */}
      {featured && <FeaturedRelease update={featured} />}

      {/* Timeline */}
      <section ref={timelineRef} className="relative container mx-auto px-4 py-20">
        <div className="mb-12 text-center">
          <h2 className="font-headline text-3xl font-bold md:text-4xl">
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Toda la historia</span>
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">Cada versión del Mod Cofrade, desde la primera cruz de guía.</p>
        </div>

        <div
          className="absolute left-1/2 top-0 hidden h-full w-1.5 -translate-x-1/2 rounded-full opacity-40 md:block"
          style={{
            backgroundImage:
              'repeating-linear-gradient(180deg, hsl(var(--primary)/0.7) 0px, hsl(var(--primary)/0.7) 10px, transparent 10px, transparent 20px)',
          }}
        />
        <motion.div
          className="absolute left-1/2 top-0 hidden w-1.5 -translate-x-1/2 rounded-full shadow-[0_0_12px_rgba(250,204,21,0.6)] md:block"
          style={{
            height: lineHeight,
            backgroundImage:
              'repeating-linear-gradient(180deg, #fbbf24 0px, #fbbf24 10px, #f59e0b 10px, #f59e0b 20px)',
          }}
        />

        <div className="space-y-4 md:space-y-6">
          {timelineUpdates.map((update, index, arr) => (
            <UpdateCard key={update.version} update={update} index={index} isLast={index === arr.length - 1} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20">
        <div className="absolute -bottom-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
        <motion.div
          className="container relative mx-auto px-4 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="font-headline text-3xl font-bold md:text-4xl">
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Mantente al Día</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            Únete a nuestro servidor de Discord para recibir las últimas noticias, participar en la comunidad y no perderte ninguna actualización del Mod Cofrade.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="font-semibold">
              <a href="https://discord.gg/KTMgF8pjz4" target="_blank" rel="noopener noreferrer">
                <ArrowRight className="h-4 w-4" />
                Únete al Discord del Mod Cofrade
              </a>
            </Button>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
